import { useState } from 'react'
import { Box, Flex, Link, Text } from '@chakra-ui/react'
import { SiShopify, SiHubspot } from 'react-icons/si'

import IconLock from '@/components/icons/IconLock'

import ProUpgradeModal from './pro-upgrade-modal'

import type { PluginData } from './pro-upgrade-modal'

const plugins: PluginData[] = [
  {
    name: 'Shopify',
    category: 'ecommerce',
    description:
      'Push designs directly to your Shopify store as product listings',
    accentColor: '#96BF48',
    icon: SiShopify,
    features: [
      'One-click publish to your Shopify storefront',
      'Auto-generate product mockups and descriptions',
      'Sync inventory and pricing in real time'
    ]
  },
  {
    name: 'HubSpot',
    category: 'crm',
    description:
      'Track design engagement and sync to HubSpot contacts',
    accentColor: '#FF7A59',
    icon: SiHubspot,
    features: [
      'Sync design activity to HubSpot contact timelines',
      'Trigger workflows based on design engagement',
      'Enrich deal records with visual assets'
    ]
  }
]

interface ProPluginsPanelProps {
  hideHeader?: boolean
}

export default function ProPluginsPanel({ hideHeader }: ProPluginsPanelProps) {
  const [selectedPlugin, setSelectedPlugin] = useState<PluginData | null>(null)

  return (
    <>
      <Box>
        {!hideHeader && (
          <Flex align="center" gap="6px" mb="10px">
            <Text textStyle="label" color="whiteAlpha.400">
              Integrations
            </Text>
            <Text
              textStyle="label"
              fontWeight={600}
              px="6px"
              py="2px"
              borderRadius="9999px"
              bg="rgba(255,255,255,0.06)"
              color="whiteAlpha.700"
              border="1px solid"
              borderColor="rgba(255,255,255,0.12)"
              textTransform="uppercase"
            >
              PRO
            </Text>
          </Flex>
        )}

        {/* Plugin list */}
        <Flex direction="column" gap="6px">
          {plugins.map((plugin) => {
            const Icon = plugin.icon
            return (
              <Flex
                key={plugin.name}
                align="center"
                gap="8px"
                px="8px"
                py="7px"
                borderRadius="8px"
                border="1px solid"
                borderColor="rgba(255,255,255,0.06)"
                bg="rgba(255,255,255,0.02)"
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{
                  bg: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.1)'
                }}
                onClick={() => setSelectedPlugin(plugin)}
              >
                <Flex
                  w="26px"
                  h="26px"
                  borderRadius="7px"
                  bg={`${plugin.accentColor}20`}
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon size={13} color={plugin.accentColor} />
                </Flex>
                <Text textStyle="bodySuperSmall" color="whiteAlpha.800" flex={1}>
                  {plugin.name}
                </Text>
                <IconLock boxSize="12px" color="whiteAlpha.300" />
              </Flex>
            )
          })}
        </Flex>

        {/* CTA */}
        <Link
          href="mailto:michael@spacerunners.com"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mt="12px"
          h="30px"
          textStyle="bodySuperSmall"
          fontWeight={400}
          borderRadius="7px"
          bg="rgba(255,255,255,0.04)"
          color="whiteAlpha.600"
          border="1px solid"
          borderColor="rgba(255,255,255,0.06)"
          transition="all 0.2s ease"
          _hover={{
            color: 'whiteAlpha.900',
            bg: 'rgba(255,255,255,0.08)',
            textDecoration: 'none'
          }}
        >
          Unlock Pro Integrations
        </Link>
      </Box>

      <ProUpgradeModal
        isOpen={!!selectedPlugin}
        onClose={() => setSelectedPlugin(null)}
        plugin={selectedPlugin}
      />
    </>
  )
}
